import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { api } from "@/lib/apiClient";
import { SitePlanCanvas } from "@/components/site-plan/SitePlanCanvas";
import type { PitchHighlightRole } from "@/lib/sitePlan";
import {
  type SitePlanPitchStatus,
  type SitePlanSettings,
  type SitePlanStatusResponse,
} from "@/lib/sitePlan";
import {
  fetchPitchOptions,
  isPitchWorkable,
  type PitchOption,
  type PitchOptionsResponse,
  utilizationLabelText,
} from "@/lib/pitchAssignmentOptions";
import type { Booking } from "@/types/camping";

interface PitchOptionPickerProps {
  booking: Booking;
  selectedPitchId: string;
  onSelectPitch: (pitchId: string, option: PitchOption) => void;
  onBlockedPitchSelect: (option: PitchOption) => void;
  /** Vorgeladene Optionen aus dem Hintergrund (z. B. vom PitchPlanPanel). */
  preloadedOptions?: PitchOptionsResponse | null;
}

function sortOptions(options: PitchOption[]): PitchOption[] {
  return [...options].sort((a, b) => {
    const rankA = a.utilization_rank ?? Number.MAX_SAFE_INTEGER;
    const rankB = b.utilization_rank ?? Number.MAX_SAFE_INTEGER;
    if (rankA !== rankB) return rankA - rankB;
    return a.code.localeCompare(b.code, "de");
  });
}

export function PitchOptionPicker({
  booking,
  selectedPitchId,
  onSelectPitch,
  onBlockedPitchSelect,
  preloadedOptions = null,
}: PitchOptionPickerProps) {
  const [planImage, setPlanImage] = useState<string | null>(null);
  const [status, setStatus] = useState<SitePlanStatusResponse | null>(null);
  const [options, setOptions] = useState<PitchOption[]>(preloadedOptions?.options ?? []);
  const [loading, setLoading] = useState(!preloadedOptions);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"plan" | "list">("plan");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const pitchOptionsPromise = preloadedOptions
        ? Promise.resolve(preloadedOptions)
        : fetchPitchOptions(booking.id);
      const [settings, planStatus, pitchOptions] = await Promise.all([
        api.get<SitePlanSettings>("/api/v1/settings/site-plan?include_image=true"),
        api.get<SitePlanStatusResponse>(
          `/api/v1/site-plan/status?reference_date=${encodeURIComponent(booking.arrival_date)}`,
        ),
        pitchOptionsPromise,
      ]);
      setPlanImage(settings.image_data);
      setStatus(planStatus);
      setOptions(pitchOptions.options);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Stellplatzoptionen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [booking.id, booking.arrival_date, preloadedOptions]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (preloadedOptions?.options) {
      setOptions(preloadedOptions.options);
      setLoading(false);
    }
  }, [preloadedOptions]);

  const optionById = useMemo(
    () => new Map(options.map((option) => [option.pitch_id, option])),
    [options],
  );

  const immediateIds = useMemo(
    () => options.filter((o) => o.selectable).map((o) => o.pitch_id),
    [options],
  );

  const relocatableIds = useMemo(
    () =>
      options
        .filter((o) => !o.selectable && o.availability_kind === "with_relocation")
        .map((o) => o.pitch_id),
    [options],
  );

  const selectableIds = useMemo(
    () => options.filter((o) => isPitchWorkable(o)).map((o) => o.pitch_id),
    [options],
  );

  const visiblePitchIds = useMemo(() => {
    if (!status) return new Set<string>();
    return new Set(
      status.pitches
        .filter(
          (p) =>
            optionById.has(p.pitch_id) &&
            p.map_polygon &&
            p.map_polygon.length >= 3,
        )
        .map((p) => p.pitch_id),
    );
  }, [status, optionById]);

  const pitchHighlights = useMemo(() => {
    const map: Record<string, PitchHighlightRole> = {};
    if (!status) return map;
    for (const pitch of status.pitches) {
      if (!visiblePitchIds.has(pitch.pitch_id)) continue;
      const option = optionById.get(pitch.pitch_id);
      if (!option) continue;
      if (pitch.pitch_id === selectedPitchId) {
        map[pitch.pitch_id] = "assigned";
      } else if (option.utilization_label === "optimal") {
        map[pitch.pitch_id] = "candidate";
      } else if (isPitchWorkable(option)) {
        map[pitch.pitch_id] = "none";
      } else {
        map[pitch.pitch_id] = "dimmed";
      }
    }
    return map;
  }, [status, selectedPitchId, visiblePitchIds, optionById]);

  const overlays = useMemo(() => {
    if (!status) return [];
    return status.pitches
      .filter((p): p is SitePlanPitchStatus & { map_polygon: NonNullable<SitePlanPitchStatus["map_polygon"]> } =>
        Boolean(visiblePitchIds.has(p.pitch_id) && p.map_polygon && p.map_polygon.length >= 3),
      )
      .map((p) => ({
        id: p.pitch_id,
        code: p.code,
        map_polygon: p.map_polygon,
        status_kind: p.status_kind,
        transition_hint: p.transition_hint,
        booking_status: p.booking_status,
      }));
  }, [status, visiblePitchIds]);

  const listOptions = useMemo(() => sortOptions(options), [options]);

  function handlePitchSelect(pitchId: string) {
    const option = optionById.get(pitchId);
    if (!option) return;
    if (option.selectable) {
      onSelectPitch(pitchId, option);
    } else if (option.availability_kind === "with_relocation") {
      onBlockedPitchSelect(option);
    } else {
      onBlockedPitchSelect(option);
    }
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Stellplatzoptionen werden geladen…</p>;
  }

  if (error) {
    return <p className="text-sm text-status-red">{error}</p>;
  }

  const blockedCount = options.length - immediateIds.length - relocatableIds.length;
  const optimalOption = options.find((o) => o.utilization_label === "optimal");

  return (
    <div className="space-y-3">
      <p className="text-xs text-label-tertiary">
        {immediateIds.length} sofort frei
        {relocatableIds.length > 0 ? ` · ${relocatableIds.length} mit Umplanung möglich` : ""}
        {blockedCount > 0 ? ` · ${blockedCount} nicht verfügbar` : ""}
        {optimalOption ? ` · Empfehlung: ${optimalOption.code}` : ""}
      </p>
      <div className="flex justify-end">
        <div className="inline-flex rounded-lg border border-separator p-0.5 text-xs">
          <button
            type="button"
            className={clsx(
              "rounded-md px-2.5 py-1 font-medium",
              viewMode === "plan" ? "bg-accent text-label-on-accent" : "text-label-secondary",
            )}
            onClick={() => setViewMode("plan")}
          >
            Plan
          </button>
          <button
            type="button"
            className={clsx(
              "rounded-md px-2.5 py-1 font-medium",
              viewMode === "list" ? "bg-accent text-label-on-accent" : "text-label-secondary",
            )}
            onClick={() => setViewMode("list")}
          >
            Liste
          </button>
        </div>
      </div>

      {viewMode === "plan" ? (
        <SitePlanCanvas
          imageData={planImage}
          pitches={overlays}
          badgeScale={status ? Number(status.badge_scale) || 1 : 1}
          mode="view"
          selectedPitchId={selectedPitchId || null}
          pitchHighlights={pitchHighlights}
          selectablePitchIds={selectableIds}
          dimmedPitchIds={options.filter((o) => !isPitchWorkable(o)).map((o) => o.pitch_id)}
          onPitchSelect={handlePitchSelect}
          onDimmedPitchSelect={handlePitchSelect}
        />
      ) : (
        <ul className="max-h-64 space-y-1 overflow-y-auto rounded-xl border border-separator p-2">
          {listOptions.map((option) => {
            const selected = selectedPitchId === option.pitch_id;
            const workable = isPitchWorkable(option);
            const utilizationText = utilizationLabelText(option.utilization_label);
            const statusText = option.selectable
              ? utilizationText ?? "frei"
              : option.availability_kind === "with_relocation"
                ? utilizationText ?? "Mit Umplanung"
                : option.problems.some((problem) => problem.kind === "category_mismatch")
                  ? "andere Kategorie"
                  : "Konflikt";
            return (
              <li key={option.pitch_id}>
                <button
                  type="button"
                  className={clsx(
                    "flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm",
                    selected
                      ? "bg-accent text-label-on-accent"
                      : workable
                        ? "hover:bg-fill-tertiary text-label"
                        : "text-label-tertiary opacity-60 hover:bg-fill-tertiary/60",
                  )}
                  onClick={() => handlePitchSelect(option.pitch_id)}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="font-medium">{option.code}</span>
                    {option.utilization_label === "optimal" ? (
                      <span
                        className={clsx(
                          "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                          selected
                            ? "bg-label-on-accent/20 text-label-on-accent"
                            : "bg-status-green/15 text-status-green",
                        )}
                      >
                        Empfohlen
                      </span>
                    ) : null}
                  </span>
                  <span
                    className={clsx(
                      "shrink-0 text-right text-xs",
                      selected ? "opacity-90" : "text-label-secondary",
                    )}
                  >
                    {statusText}
                    {option.label ? ` · ${option.label}` : ""}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
